import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, Tooltip, Legend, ArcElement } from "chart.js";
import { Link } from "react-router-dom";
import { FaAngleRight } from 'react-icons/fa';

ChartJS.register(Tooltip, Legend, ArcElement);

function Swp() {
  const [initialInvestment, setInitialInvestment] = useState(500000);
  const [withdrawalAmount, setWithdrawalAmount] = useState(10000);
  const [rateOfReturn, setRateOfReturn] = useState(8);
  const [years, setYears] = useState(10);
  const [remainingBalance, setRemainingBalance] = useState(null);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);

  const [graphData, setGraphData] = useState({
    labels: ["Remaining Balance", "Total Withdrawals"],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ["#1E3A8A", "#D1D5DB"],
      },
    ],
  });

  const calculateSWP = () => {
    const monthlyRate = rateOfReturn / 12 / 100;
    const months = years * 12;
    let balance = initialInvestment;
    let totalWithdrawn = 0;

    for (let i = 0; i < months; i++) {
      balance = balance * (1 + monthlyRate) - withdrawalAmount;
      if (balance < 0) {
        balance = 0;
        totalWithdrawn += withdrawalAmount * (i + 1);
        break;
      }
      totalWithdrawn += withdrawalAmount;
    }

    setRemainingBalance(balance.toFixed(2));
    setTotalWithdrawals(totalWithdrawn.toFixed(2));

    setGraphData({
      labels: ["Remaining Balance", "Total Withdrawals"],
      datasets: [
        {
          data: [balance, totalWithdrawn],
          backgroundColor: ["#1E3A8A", "#D1D5DB"],
        },
      ],
    });
  };

  return (
    <React.Fragment>
      <section className="bg-darkBlue pt-20 md:pt-14 text-white min-h-[40vh] md:min-h-[40vh] flex items-center justify-center">
        <div className="max-w-7xl px-4 mx-auto">
          <div className="grid grid-cols-12 items-center justify-center">
            <div className="col-span-12 text-center">
              <div className="py-12">
                <h2 className="text-2xl md:text-[40px] text-center leading-none font-bold mb-6 pt-20 md:pt-14">
                SWP
                </h2>
                <p className="text-sm text-center opacity-80 z-0 flex justify-center items-center">
                  <Link to="/" className="flex items-center text-white hover:text-gray-400">
                    Home
                  </Link>
                  <FaAngleRight className="mx-2" />
                  <Link to="/services/calculator/swp" className="text-white hover:text-gray-400">
                  SWP
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="container max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Calculator Section */}
        <div className="p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            SWP Calculator
          </h2>

          {/* Initial Investment */}
          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-700 mb-2">
              Initial Investment Amount (₹):
            </label>
            <input
              type="number"
              min="10000"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
            />
            <input
              type="range"
              min="10000"
              max="10000000"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(Number(e.target.value))}
              className="w-full h-2 bg-[#1E3A8A] rounded-lg  cursor-pointer"
            />
          </div>

          {/* Monthly Withdrawal */}
          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-700 mb-2">
              Monthly Withdrawal Amount (₹):
            </label>
            <input
              type="number"
              min="100"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
            />
            <input
              type="range"
              min="1000"
              max="100000"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(Number(e.target.value))}
              className="w-full h-2 bg-[#1E3A8A] rounded-lg  cursor-pointer"
            />
          </div>

          {/* Expected Annual Rate of Return */}
          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-700 mb-2">
              Expected Annual Rate of Return (%):
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={rateOfReturn}
              onChange={(e) => setRateOfReturn(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
            />
            <input
              type="range"
              min="1"
              max="20"
              value={rateOfReturn}
              onChange={(e) => setRateOfReturn(Number(e.target.value))}
              className="w-full h-2 bg-[#1E3A8A] rounded-lg  cursor-pointer"
            />
          </div>

          {/* Number of Years */}
          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-700 mb-2">
              Number of Years:
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
            />
            <input
              type="range"
              min="1"
              max="30"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full h-2 bg-[#1E3A8A] rounded-lg  cursor-pointer"
            />
          </div>

          <button
            onClick={calculateSWP}
            className="w-full bg-[#1E3A8A] text-white font-semibold py-3 px-8 rounded-md hover:bg-blue-700"
          >
            Calculate
          </button>

          {remainingBalance !== null && (
            <div className="mt-6 bg-green-100 p-4 rounded-md text-green-800">
              <h3 className="text-lg font-semibold">
                Remaining Balance: ₹{remainingBalance}
              </h3>
              <h3 className="text-lg font-semibold">
                Total Withdrawals: ₹{totalWithdrawals}
              </h3>
            </div>
          )}
        </div>

        {/* Pie Chart Section */}
        <div className="p-8 rounded-lg flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
            SWP Breakdown
          </h2>
          <div className="w-64 h-64">
            <Pie data={graphData} />
          </div>
        </div>
      </div>
    </div>
    </React.Fragment>
  );
}

export default Swp;
